// 홈스쿨링 - 본사 - 지사별 통계
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import axios from '@app/lib/axios';
import { format, addDays, isAfter } from "date-fns";

import CommonHorizontalBarChart from "@app/components/CommonHorizontalBarChart";

import flyengStyle from "../../components/style";

import CardStatContent, { CardStatBranchContent } from "../../components/CardStatContent";

const BranchContent = forwardRef(({ branchList, statPeriod, selectedSubOption = "content", updateViewSubOption, onViewSubOptionSelected }, ref) => {
    const [subOptionApplied, setSubOptionApplied] = useState(false);
    const [stat, setStat] = useState(false);
    const [branchCount, setBranchCount] = useState(0); // 테스트 지사가 아닌 지사 개수
    const [totalCount, setTotalCount] = useState(false);
    const [totalCategory, setTotalCategory] = useState([0, 0, 0, 0, 0]);

    useImperativeHandle(ref, () => ({
        getExcelData(callback) {
            const excelData = {
                fileName: `플라잉홈_지사콘텐츠(${selectedSubOption === "content" ? "순" : "누적"})통계`,
                sheets: [
                    {
                        name: "콘텐츠",
                        title: `홈스쿨링 지사콘텐츠(${selectedSubOption === "content" ? "순" : "누적"}) 통계`,
                        columnTitle: ["지사", "교실영어", "영상", "동화", "액티비티", "AI", `클래스(${selectedSubOption === "content" ? "순" : "누적"}) 실행 수`],
                        data: stat ? stat.map((s) => [s.branchName, s.playCountClass, s.playCount3, s.playCount3, s.playCount4, s.playCount5, s.playCount6, s.totalClass]) : [],
                    },
                ],
            };
            callback(null, excelData);
        },
    }));

    const getStat = useCallback(() => {
        setStat(false);
        if (selectedSubOption != "-") {
            axios({
                method: "POST",
                url: `/api/flyeng_stat/new/home/branch/content`,
                data: {
                    startDate: statPeriod.startDate,
                    endDate: statPeriod.endDate,
                    periodType: statPeriod.type,
                    type: selectedSubOption,
                },
            })
                .then((result) => {
                    if (result.data.code === 200) {
                        const statClass = result.data.data.classBranchContent;
                        const statHome = result.data.data.homeBranchContent;
                        const stat = [];
                        let totalCount = 0;
                        let totalCategory = [0, 0, 0, 0, 0];
                        const branchCount = branchList.reduce((ac, current) => ac + (current.type == "B" ? 1 : 0), 0);

                        if (statHome && statHome.length > 0) {
                            statHome.forEach((s) => {
                                const branch = branchList.find((b) => b.id === s.branchId);
                                if (branch && branch.type == "B") {
                                    // TODO: check - 일반 지사만 통계에 포함?
                                    s.branchName = branch.name;
                                    stat.push({ ...s, branchName: branch.name });
                                }
                            });
                            totalCount = stat.reduce((ac, current) => ac + (current.total ? current.total : 0), 0);
                            totalCategory[0] = stat.reduce((ac, current) => ac + (current.playCountClass ? current.playCountClass : 0), 0);
                            for (let i = 3; i <= 6; i++) {
                                totalCategory[i - 2] = stat.reduce((ac, current) => ac + (current[`playCount${i}`] ? current[`playCount${i}`] : 0), 0);
                            }
                            if (statClass && statClass.length > 0) {
                                statClass.forEach((s) => {
                                    const branch = stat.find((c) => c.branchId === s.branchId);
                                    if (branch) {
                                        branch.totalClass = s.total;
                                    }
                                });
                            }
                        }

                        setStat(stat);
                        setBranchCount(branchCount);
                        setTotalCount(totalCount);
                        setTotalCategory(totalCategory);
                    } else {
                        alert(result.data && result.data.msg ? result.data.msg : "통계 정보를 가져오는데 실패하였습니다.\n지속 발생시 관리자에게 문의해주세요.");
                    }
                })
                .catch((error) => {
                    console.log(error);
                    console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
                });
        }
    }, [statPeriod, selectedSubOption]);
    useEffect(() => {
        if (!subOptionApplied && updateViewSubOption) {
            updateViewSubOption(
                [
                    { name: "콘텐츠 순 실행 수", id: "content" },
                    { name: "콘텐츠 누적 실행 수", id: "sum" },
                ],
                "content"
            );
            setSubOptionApplied(true);
        }
        getStat();
        return () => {};
    }, [getStat, subOptionApplied]);
    const series = flyengStyle.seriesHomePlayChart.map((s) => Object.assign({}, s));
    if (series.length < 6) {
        series.push({ name: "클래스 합계", field: "totalClass", stack: "line", type: "line", option: { itemStyle: { color: "#d87a80" } } });
    }
    const chartOption = {
        xAxis: {
            field: "branchName",
            option: {
                axisLabel: {
                    show: true,
                    interval: 0,
                    rotate: 45,
                },
            },
        },
        yAxis: {
            name: "건",
            series: series,
        },
        stacks: [
            { stack: "base", title: `홈스쿨링 ${selectedSubOption === "content" ? "순" : "누적"} 실행 수`, showSum: false, showSumTooltip: true, showPercent: true },
            { stack: "line", title: `클래스 ${selectedSubOption === "content" ? "순" : "누적"} 실행 수`, showSum: false, showPercent: false },
        ],
        showLegend: true,
        legend: {
            x: "right",
            y: "top",
        },
        sort: false,
        grid: {
            top: "10%",
            left: "5%",
            right: "5%",
            bottom: "15%",
        },
    };
    const typeTitle = selectedSubOption === "content" ? "순" : "누적";
    const branchTop = stat && stat.length > 0 ? stat[0] : null;
    const branchLast = stat && stat.length > 0 ? stat[stat.length - 1] : null;
    const avgTotal = (totalCount / branchCount).toFixed(1);
    const avgCategory = totalCategory.map((t) => (t / branchCount).toFixed(1));

    return (
        <div className="stat-branch-content">
            <div className="row mb-2">
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatContent
                        id="1"
                        title={`전체 콘텐츠 ${typeTitle} 실행 수`}
                        titleTotal="합계"
                        total={totalCount.toLocaleString()}
                        average={avgTotal.toLocaleString()}
                        titleBgClass="bg-tomonote"
                    />
                </div>
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatContent
                        id="2"
                        title={`교실영어 ${typeTitle} 실행 수`}
                        titleTotal="합계"
                        total={totalCategory[0].toLocaleString()}
                        average={avgCategory[0].toLocaleString()}
                        titleBgClass="bg-tomonote"
                    />
                </div>
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatContent
                        id="3"
                        title={`영상 ${typeTitle} 실행 수`}
                        titleTotal="합계"
                        total={totalCategory[1].toLocaleString()}
                        average={avgCategory[1].toLocaleString()}
                        titleBgClass="bg-tomonote"
                    />
                </div>
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatContent
                        id="3"
                        title={`동화 ${typeTitle} 실행 수`}
                        titleTotal="합계"
                        total={totalCategory[2].toLocaleString()}
                        average={avgCategory[2].toLocaleString()}
                        titleBgClass="bg-tomonote"
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatContent
                        id="2"
                        title={`액티비티 ${typeTitle} 실행 수`}
                        titleTotal="합계"
                        total={totalCategory[3].toLocaleString()}
                        average={avgCategory[3].toLocaleString()}
                        titleBgClass="bg-tomonote"
                    />
                </div>
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatContent
                        id="3"
                        title={`AI ${typeTitle} 실행 수`}
                        titleTotal="합계"
                        total={totalCategory[4].toLocaleString()}
                        average={avgCategory[4].toLocaleString()}
                        titleBgClass="bg-tomonote"
                    />
                </div>
                <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
                    <CardStatBranchContent id="4" top={branchTop} last={branchLast} />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <div className="card">
                        <div className="card-body" style={{ height: "500px", textAlign: "center" }}>
                            <CommonHorizontalBarChart chartData={stat} chartOption={chartOption} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default BranchContent;
